// src/components/ui/Navbar.jsx — v4 Auth-aware (Phase 2.25F-Hotfix)
import { useState, useEffect, useRef } from "react";
import { NAVBAR_H } from "../../App";
import { Logo, Btn } from "./UI";
import { BRAND } from "../../config";
import PortalIcon from "./PortalIcon";
import NotificationBell from "./NotificationBell";

const NAV_LINKS = [
  ["Store",   "store"],
  ["Search",  "search"],
  ["Courses", "courses"],
  ["Blog",    "blog"],
  ["About",   "about"],
  ["Contact", "contact"],
];

const STORE_ACTIVE_PAGES = ["store","services","bundles","materials","branding-store"];

export default function Navbar({ page, setPage, dark, toggleTheme, t, onPortalClick, onAccountClick, user, profile, onLogout }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  const go = (p) => { setPage(p); setOpen(false); setDropdownOpen(false); window.scrollTo(0, 0); };

  const isActive = (id) => id === "store" ? STORE_ACTIVE_PAGES.includes(page) : page === id;

  const rawName = profile?.ownerName || profile?.coachingName || profile?.displayName || user?.displayName || user?.email?.split("@")[0] || "My Account";
  const displayName = String(rawName).split(/\s+/)[0] || "My Account";
  const isAdmin = profile?.role === "admin";
  const isLoggedIn = !!(user && profile);
  const hasStudentAccess = !!profile && (
    profile.role === "student" ||
    profile.userType === "student" ||
    profile.isAlsoStudent
  );

  const goAccount = (tab = "overview") => {
    setOpen(false);
    setDropdownOpen(false);
    if (onAccountClick) {
      onAccountClick(tab);
    } else {
      setPage("my-account");
    }
    window.dispatchEvent(new CustomEvent("dgh:account-tab", { detail: { tab } }));
  };

  const navBtn = (active) => ({
    background: active ? `${t.gold}14` : "transparent",
    border: `1px solid ${active ? `${t.gold}35` : "transparent"}`,
    cursor: "pointer",
    padding: "7px 12px",
    borderRadius: 999,
    fontSize: ".83rem",
    fontWeight: active ? 800 : 600,
    color: active ? t.gold : t.muted,
    transition: "background .2s ease, border-color .2s ease, color .2s ease",
    whiteSpace: "nowrap",
  });

  const mobileNavBtn = (active) => ({
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "12px 20px",
    background: active ? `${t.gold}12` : "none",
    border: "none",
    borderLeft: active ? `3px solid ${t.gold}` : "3px solid transparent",
    fontSize: ".96rem",
    fontWeight: active ? 800 : 500,
    color: active ? t.gold : t.text,
    cursor: "pointer",
    textAlign: "left",
  });

  const handleLogout = () => {
    setDropdownOpen(false);
    setOpen(false);
    if (onLogout) onLogout();
  };

  return (
    <>
      <style>{`
        @media(max-width:900px){.nav-links{display:none!important;}.nav-ham{display:flex!important;}}
        @media(min-width:901px){.nav-ham{display:none!important;}}
        .nav-pill:hover{background:${t.bgCard2}!important;color:${t.text}!important;border-color:${t.border}!important;transform:translateY(-1px);}
        .nav-pill[aria-current="page"]:hover{background:${t.gold}18!important;color:${t.gold}!important;border-color:${t.gold}45!important;}
        .nav-acct-dd { position:absolute; top:calc(100% + 6px); right:0; min-width:190px; background:${t.bgCard}; border:1px solid ${t.border}; border-radius:12px; box-shadow:0 8px 24px rgba(0,0,0,.18); z-index:2000; padding:6px 0; }
        .nav-acct-item { display:flex; align-items:center; gap:9px; width:100%; padding:10px 16px; background:none; border:none; color:${t.text}; font-size:.84rem; font-weight:700; cursor:pointer; font-family:inherit; text-align:left; white-space:nowrap; transition:background .14s; }
        .nav-acct-item:hover { background:${t.bgCard2}; }
        .nav-acct-divider { height:1px; background:${t.border}; margin:4px 0; }
      `}</style>

      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        background: scrolled ? t.nav : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? `1px solid ${t.border}` : "none",
        transition: "all .3s",
      }}>
        <nav style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          height: NAVBAR_H, padding: "0 clamp(16px,4%,40px)",
          maxWidth: 1400, margin: "0 auto",
        }}>
          {/* Logo */}
          <div onClick={() => go("home")} style={{ cursor: "pointer", flexShrink: 0 }}
            role="button" aria-label="Go to home page" tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && go("home")}>
            <Logo t={t} size={38}/>
          </div>

          {/* Desktop nav */}
          <ul className="nav-links" style={{ display: "flex", gap: 2, listStyle: "none", margin: 0, padding: 0, alignItems: "center" }}
            role="menubar" aria-label="Main navigation">

            <li role="none">
              <button className="nav-pill" onClick={() => go("home")} style={navBtn(page === "home")}
                role="menuitem" aria-current={page === "home" ? "page" : undefined}>
                Home
              </button>
            </li>

            {NAV_LINKS.map(([label, id]) => (
              <li key={id} role="none">
                <button className="nav-pill" onClick={() => go(id)} style={navBtn(isActive(id))}
                  role="menuitem" aria-current={isActive(id) ? "page" : undefined}>
                  {label}
                </button>
              </li>
            ))}
          </ul>

          {/* Right controls */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <button onClick={toggleTheme} style={{
              width: 36, height: 36, borderRadius: 10,
              border: `1px solid ${t.border}`, background: t.bgCard,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: dark ? "#94A3B8" : "#64748B",
              cursor: "pointer", flexShrink: 0, transition: "all .18s ease",
            }}
              aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
              title={dark ? "Light mode" : "Dark mode"}
              onMouseEnter={e => { e.currentTarget.style.background = t.bgCard2; e.currentTarget.style.color = t.text; }}
              onMouseLeave={e => { e.currentTarget.style.background = t.bgCard;  e.currentTarget.style.color = dark ? "#94A3B8" : "#64748B"; }}>
              <PortalIcon name={dark ? "sun" : "moon"} size={16} stroke={2}/>
            </button>

            {isLoggedIn && !isAdmin && (
              <NotificationBell t={t} />
            )}

            {isLoggedIn ? (
              <div ref={dropdownRef} style={{ position: "relative" }}>
                <button
                  onClick={() => setDropdownOpen(o => !o)}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 8, background: `${t.gold}12`, border: `1px solid ${t.gold}30`, color: t.gold, fontWeight: 700, fontSize: ".78rem", cursor: "pointer", whiteSpace: "nowrap", maxWidth: 150 }}
                  aria-label="Account menu"
                  aria-expanded={dropdownOpen}>
                  <PortalIcon name="user" size={13}/>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", maxWidth: 90 }}>{displayName}</span>
                  <PortalIcon name="chevronDown" size={11} style={{ opacity: .6, flexShrink: 0 }}/>
                </button>

                {dropdownOpen && (
                  <div className="nav-acct-dd" role="menu">
                    <div style={{ padding: "8px 16px 6px", borderBottom: `1px solid ${t.border}`, marginBottom: 4 }}>
                      <div style={{ color: t.text, fontWeight: 800, fontSize: ".82rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</div>
                      {user?.email && <div style={{ color: t.muted, fontSize: ".72rem", marginTop: 1 }}>{user.email}</div>}
                    </div>

                    {isAdmin && (
                      <button className="nav-acct-item" role="menuitem" onClick={() => go("home")}>
                        <PortalIcon name="dashboard" size={14}/> Admin Dashboard
                      </button>
                    )}

                    {!isAdmin && (
                      <button className="nav-acct-item" role="menuitem" onClick={() => goAccount("overview")}>
                        <PortalIcon name="user" size={14}/> My Account
                      </button>
                    )}
                    {!isAdmin && (
                      <>
                        <button className="nav-acct-item" role="menuitem" onClick={() => goAccount("branding")}>
                          <PortalIcon name="bag" size={14}/> Branding Orders
                        </button>
                        <button className="nav-acct-item" role="menuitem" onClick={() => goAccount("downloads")}>
                          <PortalIcon name="download" size={14}/> Downloads
                        </button>
                        <button className="nav-acct-item" role="menuitem" onClick={() => goAccount("purchases")}>
                          <PortalIcon name="package" size={14}/> Purchases
                        </button>
                        <button className="nav-acct-item" role="menuitem" onClick={() => goAccount("notifications")}>
                          <PortalIcon name="bell" size={14}/> Notifications
                        </button>
                        <button className="nav-acct-item" role="menuitem" onClick={() => goAccount("support")}>
                          <PortalIcon name="shield" size={14}/> Support
                        </button>
                      </>
                    )}
                    {!isAdmin && hasStudentAccess && (
                      <button className="nav-acct-item" role="menuitem" onClick={() => go("student-portal")}>
                        <PortalIcon name="course" size={14}/> Student Portal
                      </button>
                    )}

                    <div className="nav-acct-divider"/>
                    <button className="nav-acct-item" role="menuitem" onClick={handleLogout}
                      style={{ color: "#EF4444" }}>
                      <PortalIcon name="logout" size={14}/> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
                <button onClick={onPortalClick || (() => setPage("login"))}
                style={{ padding: "6px 12px", borderRadius: 8, background: t.accentBg, border: `1px solid ${t.accent}30`, color: t.accent, fontWeight: 700, fontSize: ".78rem", cursor: "pointer", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 5 }}
                aria-label="Login to your account">
                <PortalIcon name="login" size={13}/>Login
              </button>
            )}

            <div className="nav-links">
              <Btn href={`https://wa.me/${BRAND.whatsapp}`} variant="primary" t={t}
                style={{ padding: "8px 12px", fontSize: ".8rem", borderRadius: 10 }}
                aria-label="Contact us on WhatsApp">
                <PortalIcon name="phone" size={14}/>WhatsApp
              </Btn>
            </div>

            <button className="nav-ham" onClick={() => setOpen(!open)}
              style={{ width: 36, height: 36, borderRadius: 8, border: `1px solid ${t.border}`, background: t.bgCard, display: "none", alignItems: "center", justifyContent: "center", fontSize: "1.1rem", color: t.text, cursor: "pointer" }}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-menu">
              <PortalIcon name={open ? "x" : "menu"} size={18}/>
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div id="mobile-menu"
          style={{ position: "fixed", top: NAVBAR_H, left: 0, right: 0, bottom: 0, zIndex: 999, background: t.bgCard, borderTop: `1px solid ${t.border}`, overflowY: "auto", display: "flex", flexDirection: "column" }}
          role="dialog" aria-label="Mobile navigation menu">
          <div style={{ padding: "8px 0", flex: 1 }}>
            <button onClick={() => go("home")} style={mobileNavBtn(page === "home")}
              aria-current={page === "home" ? "page" : undefined}>
              Home
            </button>

            {NAV_LINKS.map(([label, id]) => (
              <button key={id} onClick={() => go(id)} style={mobileNavBtn(isActive(id))}
                aria-current={isActive(id) ? "page" : undefined}>
                {label}
              </button>
            ))}

            {isLoggedIn && (
              <>
                <div style={{ height: 1, background: t.border, margin: "6px 0" }}/>
                {isAdmin && (
                  <button onClick={() => go("home")} style={mobileNavBtn(false)}>
                    Admin Dashboard
                  </button>
                )}
                {!isAdmin && (
                  <button onClick={() => goAccount("overview")} style={mobileNavBtn(page === "my-account")}>
                    My Account
                  </button>
                )}
                {!isAdmin && (
                  <>
                    <button onClick={() => goAccount("branding")} style={mobileNavBtn(false)}>
                      Branding Orders
                    </button>
                    <button onClick={() => goAccount("downloads")} style={mobileNavBtn(false)}>
                      Downloads
                    </button>
                    <button onClick={() => goAccount("purchases")} style={mobileNavBtn(false)}>
                      Purchases
                    </button>
                    <button onClick={() => goAccount("notifications")} style={mobileNavBtn(false)}>
                      Notifications
                    </button>
                    <button onClick={() => goAccount("support")} style={mobileNavBtn(false)}>
                      Support
                    </button>
                  </>
                )}
                {!isAdmin && hasStudentAccess && (
                  <button onClick={() => go("student-portal")} style={mobileNavBtn(false)}>
                    Student Portal
                  </button>
                )}
              </>
            )}
          </div>

          <div style={{ padding: "16px 20px", borderTop: `1px solid ${t.border}`, display: "flex", flexDirection: "column", gap: 10 }}>
            {isLoggedIn ? (
              <>
                <div style={{ padding: "10px 14px", borderRadius: 10, background: `${t.gold}10`, border: `1px solid ${t.gold}25`, display: "flex", alignItems: "center", gap: 10 }}>
                  <PortalIcon name="user" size={15} style={{ color: t.gold, flexShrink: 0 }}/>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: t.gold, fontWeight: 800, fontSize: ".88rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayName}</div>
                    {user?.email && <div style={{ color: t.muted, fontSize: ".72rem" }}>{user.email}</div>}
                  </div>
                </div>
                <button onClick={handleLogout}
                  style={{ padding: "12px", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 12, color: "#EF4444", fontWeight: 700, fontSize: ".9rem", cursor: "pointer" }}>
                  Logout
                </button>
              </>
            ) : (
              <button
                onClick={() => { setOpen(false); (onPortalClick || (() => setPage("login")))(); }}
                style={{ padding: "13px", background: t.accentBg, border: `1px solid ${t.accent}30`, borderRadius: 12, color: t.accent, fontWeight: 700, fontSize: ".95rem", cursor: "pointer" }}>
                <PortalIcon name="login" size={15} style={{ marginRight: 7 }}/>Login / Register
              </button>
            )}
            <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer"
              onClick={() => setOpen(false)}
              style={{ display: "block", textAlign: "center", background: "#25D366", color: "#fff", padding: "13px", borderRadius: 12, fontWeight: 700, fontSize: ".95rem", textDecoration: "none" }}>
              <PortalIcon name="phone" size={15} style={{ marginRight: 7 }}/>Chat on WhatsApp
            </a>
          </div>
        </div>
      )}
    </>
  );
}
